// -----------------------------------------------------------------------------
// Pure helpers for the visual email-template editor.
//
// The HTML *source* (with `{{token}}` placeholders) is the single source of truth
// for the template: it is what the server renderer, the token allow-list and the
// versioning pipeline all consume. The visual ("Diseño") canvas must therefore be
// able to round-trip to that exact shape. These helpers do the two halves:
//   - `buildCanvasDoc`  : source HTML  → editable document (tokens shown as chips).
//   - `serializeEditableDocument` : edited live document → clean source HTML.
//
// Tokens are wrapped as `contenteditable="false"` chips so a stray keystroke can
// never split a `{{token}}`; on serialize each chip becomes its `{{name}}` again.
// All editor-only artefacts (the injected style tag, `contenteditable` attributes)
// are stripped so the saved HTML stays email-client-safe.
// -----------------------------------------------------------------------------

/** Matches `{{ token_name }}` (optional inner whitespace). Capture group 1 is the name. */
export const TOKEN_RE = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/** Attribute marking the editor-only `<style>` tag so serialization can drop it. */
const EDITOR_STYLE_ATTR = 'data-ds-editor-style';

/** Attribute carrying a token's name on its chip span (round-trip key). */
export const TOKEN_CHIP_ATTR = 'data-ds-token';

/** The DocuSense brand blue and its hover shade — the swappable "accent" colour. */
export const DEFAULT_ACCENT = '#046bd2';

/** Inline-styled, non-editable chip markup for a single token. */
export function tokenChipMarkup(name: string): string {
  return (
    `<span ${TOKEN_CHIP_ATTR}="${name}" contenteditable="false" ` +
    'style="display:inline-block;padding:0 6px;margin:0 1px;border-radius:9999px;' +
    'background:#e7f0fb;color:#046bd2;font:600 12px ui-monospace,SFMono-Regular,Menlo,monospace;' +
    `white-space:nowrap;">{{${name}}}</span>`
  );
}

/** Replaces every `{{token}}` in the source with a protected chip span. */
export function wrapTokensAsChips(html: string): string {
  return html.replace(TOKEN_RE, (_match, name: string) => tokenChipMarkup(name));
}

/** The editor-only stylesheet injected into the canvas (stripped on serialize). */
function editorStyleTag(): string {
  return (
    `<style ${EDITOR_STYLE_ATTR}>` +
    'html,body{margin:0;}' +
    '[contenteditable="true"]{outline:none;}' +
    `[${TOKEN_CHIP_ATTR}]{user-select:all;cursor:default;}` +
    '[contenteditable="true"] :hover{outline:1px dashed rgba(4,107,210,.35);outline-offset:2px;}' +
    '</style>'
  );
}

/**
 * Builds the editable document for the canvas from the body source HTML: wraps
 * tokens as chips, injects the editor stylesheet into <head> and makes <body>
 * editable. Tolerates a body that is a full document OR a bare fragment.
 */
export function buildCanvasDoc(sourceHtml: string): string {
  let doc = wrapTokensAsChips(sourceHtml);

  doc = /<\/head>/i.test(doc)
    ? doc.replace(/<\/head>/i, `${editorStyleTag()}</head>`)
    : `${editorStyleTag()}${doc}`;

  doc = /<body[^>]*>/i.test(doc)
    ? doc.replace(/<body([^>]*)>/i, '<body$1 contenteditable="true">')
    : `<body contenteditable="true">${doc}</body>`;

  return doc;
}

/**
 * Serializes the live, edited canvas document back to clean source HTML:
 * removes the editor stylesheet, drops every `contenteditable` attribute, and
 * turns each token chip back into its `{{name}}` literal. Operates on a clone so
 * the live document the user is editing is never mutated.
 */
export function serializeEditableDocument(doc: Document): string {
  const root = doc.documentElement.cloneNode(true) as HTMLElement;

  root.querySelectorAll(`[${EDITOR_STYLE_ATTR}]`).forEach((node) => node.remove());
  root.querySelectorAll('[contenteditable]').forEach((node) => node.removeAttribute('contenteditable'));
  root.querySelectorAll(`[${TOKEN_CHIP_ATTR}]`).forEach((chip) => {
    const name = chip.getAttribute(TOKEN_CHIP_ATTR) ?? '';
    chip.replaceWith(doc.createTextNode(`{{${name}}}`));
  });

  return `<!doctype html>\n${root.outerHTML}`;
}

/**
 * Swaps one hex colour for another across the whole HTML (case-insensitive).
 * Used by the accent-colour control to recolour the brand blue without touching
 * the multi-stop logo gradient (whose stops are different hexes). No-op when the
 * two colours are equal.
 */
export function replaceColor(html: string, fromHex: string, toHex: string): string {
  if (fromHex.toLowerCase() === toHex.toLowerCase()) {
    return html;
  }
  const escaped = fromHex.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return html.replace(new RegExp(escaped, 'gi'), toHex);
}

/**
 * Returns the first candidate colour that appears in the HTML, or the default
 * accent when none is present — so the colour picker opens on the colour the
 * template actually uses.
 */
export function detectAccentColor(html: string, candidates: readonly string[] = [DEFAULT_ACCENT]): string {
  const lower = html.toLowerCase();
  return candidates.find((hex) => lower.includes(hex.toLowerCase())) ?? DEFAULT_ACCENT;
}
